// Run du mode Agent autonome sur le NOUVEAU système (autopilot-api : /autopilot/campaigns).
// Activé quand REACT_APP_AUTOPILOT_API_URL est renseigné ; sinon le front garde l'ancien backend.
// Seuls démarrer / arrêter / lire l'état passent ici : templates, conditions d'éligibilité et persona restent
// sur les écrans existants (le nouveau système lit les mêmes données).
import { AxiosInstanceCreator } from '../../../api/AxiosInstance';
import { IDLE_RUN_STATE, RunState, RunStatus, StartRunRequest, StopOption } from '../types';
import type { RunApi, SettingsApi } from './autonomousApi';
import { apiErrorMessage } from './httpErrors';

/** Adresse d'autopilot-api (sans le préfixe /autopilot), vide = nouveau système non branché */
export const autopilotApiUrl = (): string => (process.env.REACT_APP_AUTOPILOT_API_URL || '').trim().replace(/\/+$/, '');
export const isAutopilotApi = (): boolean => autopilotApiUrl() !== '';

/** Résumé d'une campagne renvoyé par autopilot-api */
export interface AutopilotCampaign {
    id: string;
    persona: string;
    state: 'RUNNING' | 'PAUSED' | 'STOPPING' | 'FINISHED' | 'FAILED' | string;
    reason: string | null;
    options: { mode?: 'draft' | 'live'; test_mode?: boolean; stop?: { mode?: string; max_emails?: number | null } };
    created_at: string;
    updated_at: string;
    counts: Record<string, number>;
    sent: number;
    /** Brouillons prêts (mode Brouillon : rien n'est envoyé) */
    ready?: number;
    /** Persona mal alignée sur les offres (calculé sur les premiers contacts analysés) */
    targeting_alert?: { message: string; share: number; decided: number } | null;
    mode?: 'draft' | 'live';
}

// Prospects passés par le jugement (CoT 1) ou écartés avant, et ceux retenus pour au moins une PV
const PROCESSED = ['EXCLUDED', 'NOT_ELIGIBLE', 'MATCHED', 'SCRAPED', 'DRAFTED', 'APPROVED', 'READY', 'SENT', 'DONE', 'REJECTED', 'FAILED'];
const VALIDATED = ['MATCHED', 'SCRAPED', 'DRAFTED', 'APPROVED', 'READY', 'SENT', 'DONE', 'REJECTED'];

/** Brouillon (rien n'est envoyé) ou Live ; les campagnes antérieures n'avaient que test_mode (test = Brouillon) */
export const isDraftCampaign = (c: AutopilotCampaign): boolean =>
    (c.mode || c.options?.mode) ? (c.mode || c.options?.mode) === 'draft' : (c.options?.test_mode ?? true);

/** Mails terminés : brouillons prêts + mails envoyés */
const doneMails = (c: AutopilotCampaign): number => (c.sent || 0) + (c.ready || 0);
const sum = (counts: Record<string, number>, keys: string[]) => keys.reduce((n, k) => n + (counts[k] || 0), 0);

const stopOptionOf = (c: AutopilotCampaign): StopOption =>
    c.options?.stop?.mode === 'max_emails' && c.options.stop.max_emails
        ? { stopMode: 'count', maxEmails: c.options.stop.max_emails }
        : { stopMode: 'manual' };

const statusOf = (c: AutopilotCampaign): RunStatus => {
    switch (c.state) {
        case 'RUNNING':
        case 'PAUSED':
            return 'running';
        case 'FAILED':
            return 'error';
        // En cours d'arrêt : plus rien de nouveau, seuls les mails déjà en route se terminent (ils arrivent par mail).
        // Affichée comme arrêtée : une nouvelle campagne peut être lancée tout de suite.
        case 'STOPPING':
        case 'FINISHED': {
            const stop = stopOptionOf(c);
            return stop.stopMode === 'count' && doneMails(c) >= stop.maxEmails ? 'completed' : 'stopped';
        }
        default:
            return 'idle';
    }
};

/** Campagne du nouveau système → état de run affiché par le front */
export const campaignToRunState = (c: AutopilotCampaign | null | undefined): RunState => {
    if (!c) return IDLE_RUN_STATE;
    const status = statusOf(c);
    const counts = c.counts || {};
    return {
        runId: c.id,
        status,
        stopOption: stopOptionOf(c),
        desktopStart: 'auto',
        testMode: isDraftCampaign(c),
        processedCompanies: sum(counts, PROCESSED),
        validatedCompanies: sum(counts, VALIDATED),
        sentEmails: doneMails(c),
        startedAt: c.created_at || null,
        endedAt: status === 'running' || status === 'stopping' ? null : c.updated_at || null,
        errorMessage: status === 'error' ? c.reason || 'Campagne en échec.' : null,
        targetingAlert: c.targeting_alert?.message || null,
    };
};

const http = () => AxiosInstanceCreator(autopilotApiUrl(), 'autopilot');

const call = async <T,>(request: () => Promise<any>, fallback: string): Promise<T> => {
    try {
        return (await request())?.data as T;
    } catch (e) {
        throw new Error(apiErrorMessage(e, fallback));
    }
};

const latest = async (): Promise<AutopilotCampaign | null> => {
    const list = await call<AutopilotCampaign[]>(() => http().get('campaigns', { params: { limit: 1 } }),
        "Lecture de l'état impossible.");
    return Array.isArray(list) && list.length ? list[0] : null;
};

/** Campagnes encore actives (en cours, en pause ou en cours d'arrêt) : plusieurs peuvent tourner en même temps */
export const listActiveCampaigns = async (): Promise<AutopilotCampaign[]> => {
    const list = await call<AutopilotCampaign[]>(() => http().get('campaigns', { params: { limit: 30 } }),
        'Lecture des campagnes impossible.');
    return (Array.isArray(list) ? list : []).filter((c) => ['RUNNING', 'PAUSED', 'STOPPING'].includes(c.state));
};

/** Arrêt d'une campagne précise (les mails déjà en route finissent) */
export const stopCampaign = async (id: string): Promise<AutopilotCampaign> =>
    call<AutopilotCampaign>(() => http().post(`campaigns/${encodeURIComponent(id)}/stop`), "Impossible d'arrêter la campagne.");

/** La persona Sales Navigator vient des réglages existants du mode autonome */
export const createAutopilotRunApi = (settings: Pick<SettingsApi, 'getSettings'>): RunApi => ({
    async startRun(request: StartRunRequest) {
        const persona = (await settings.getSettings()).persona?.name?.trim();
        if (!persona) throw new Error('Indiquez le nom de la persona Sales Navigator dans les réglages.');
        const stop = request.stopMode === 'count'
            ? { mode: 'max_emails', max_emails: request.maxEmails }
            : { mode: 'manual' };
        const campaign = await call<AutopilotCampaign>(
            () => http().post('campaigns', {
                persona, mode: request.testMode ? 'draft' : 'live', test_mode: request.testMode, stop, exclusions: [],
                ...(request.sourceUrl ? { source_url: request.sourceUrl } : {}),
                ...(request.minCompanySize ? { min_company_size: request.minCompanySize } : {}),
            }),
            'Impossible de lancer le run.');
        return campaignToRunState(campaign);
    },
    async stopRun() {
        const current = await latest();
        if (!current || !['RUNNING', 'PAUSED'].includes(current.state)) return campaignToRunState(current);
        return campaignToRunState(await call<AutopilotCampaign>(
            () => http().post(`campaigns/${encodeURIComponent(current.id)}/stop`), "Impossible d'arrêter le run."));
    },
    async getRunState() {
        return campaignToRunState(await latest());
    },
});

/** Activité en direct d'une campagne : ce que l'agent fait en ce moment et ses dernières actions */
export interface CampaignLive {
    campaign_id: string;
    state: string;
    in_progress: number;
    now: { step: string; label: string; count: number; names: string[] }[];
    feed: { at: string; tone: 'ok' | 'info' | 'skip' | 'warn'; state: string; text: string }[];
}

export const loadCampaignLive = (campaignId: string): Promise<CampaignLive> =>
    call<CampaignLive>(() => http().get(`campaigns/${encodeURIComponent(campaignId)}/live`),
        "Lecture de l'activité de l'agent impossible.");

// ── Triggers : signaux d'entreprises (levées de fonds, recrutements, direction, appels d'offres) ──
export interface AgentSignal {
    id: string;
    kind: 'funding' | 'leadership' | 'hiring' | 'tender' | string;
    kind_label: string;
    company: { name: string; company_id?: string; company_url?: string; industry?: string; headcount?: string };
    pv: { slug: string; title: string };
    pertinence: number;
    evenement: string;
    date: string;
    raison: string;
    accroche: string;
    source_url: string;
    status: string;
    detected_at: string;
    /** Poste à contacter, choisi parmi les cibles de l'offre retenue (sinon la persona des réglages) */
    cible?: string;
    /** Postes visés par l'offre retenue (choix proposés) et poste choisi par l'utilisateur */
    cibles?: string[];
    role?: string | null;
    type_entreprise?: string;
    /** Offres écartées pour cette entreprise, avec la raison */
    ecartees?: { pv: string; raison: string }[];
    /** Preuves de l'événement : fiche Sales Navigator, pages web, avis BOAMP */
    sources?: { type: 'sales_navigator' | 'web' | 'boamp' | 'linkedin_job' | 'linkedin_post' | 'linkedin_company' | string;
        url: string; titre: string }[];
    /** Intermédiaire (cabinet de recrutement, plateforme) : le mail ne cite pas l'annonce */
    intermediaire?: boolean;
    /** Offre corrigée par l'utilisateur */
    corrige?: boolean;
    /** Décideurs trouvés à la demande (persona des réglages) */
    contacts?: { status: 'pending' | 'done' | 'error'; items: { name: string; title: string; company: string; profile_url: string }[]; error?: string | null } | null;
    /** Appel d'offres : correspondant de l'acheteur public indiqué dans l'avis */
    correspondent?: { name: string; role?: string } | null;
}

/** Avancement d'une recherche de signaux : recherches Sales Navigator, analyse des entreprises trouvées, puis offres
 *  d'emploi LinkedIn (Surfing LinkedIn de Titan) */
export interface SignalRunProgress { phase: 'recherche' | 'analyse' | 'offres'; searches?: number; searches_done?: number;
    found?: number; to_judge?: number; judged?: number; kept?: number; jobs?: number; step?: string }
export interface SignalRun { status?: string; stats?: { examined: number; kept: number }; progress?: SignalRunProgress | null }

export const loadSignals = async (): Promise<{ items: AgentSignal[]; run: SignalRun | null }> =>
    call(() => http().get('signals'), 'Lecture des signaux impossible.');

/** Réglages d'une recherche LinkedIn lancée depuis la page des signaux (offres d'emploi, publications) */
export interface LinkedInSearch {
    queries: string[]; title_keywords: string[]; title_exclude: string[]; post_queries: string[];
    location: string; period_h: number; post_period: 'past-24h' | 'past-week' | 'past-month';
}
/** Recherche à la demande : tout (Sales Navigator, BOAMP, LinkedIn) ou, avec `linkedin`, LinkedIn seul */
export const runSignals = (linkedin?: LinkedInSearch): Promise<{ status: string }> =>
    call(() => http().post('signals/run', linkedin ? { linkedin } : {}), 'Recherche de signaux impossible.');

/** Annulation de la recherche en cours, à tout moment : « cancelled » (pas encore commencée), « cancelling » (arrêtée à
 * la prochaine étape) ou « none » ; les signaux déjà retenus restent */
export const cancelSignals = (): Promise<{ status: 'cancelled' | 'cancelling' | 'none' }> =>
    call(() => http().post('signals/run/cancel', {}), 'Annulation de la recherche impossible.');

/** Décideurs d'une entreprise signalée (une page Sales Navigator) : poste choisi (`title`), sinon poste visé par
 * l'offre retenue, sinon persona des réglages */
export const findSignalContacts = (id: string, persona: string, title = ''): Promise<{ status: string }> =>
    call(() => http().post(`signals/${encodeURIComponent(id)}/contacts`, { persona, title }), 'Recherche des décideurs impossible.');

/** Offres de l'utilisateur (propositions de valeur), pour corriger celle d'un signal */
export interface SignalOffer { slug: string; title: string; cibles: string[] }
export const loadSignalOffers = (): Promise<SignalOffer[]> =>
    call(() => http().get('signals/offers'), 'Offres indisponibles.').then((r: any) => r.items || []);

/** Correction de l'utilisateur : offre retenue et poste visé ; gardée comme exemple pour les jugements suivants */
export const correctSignal = (id: string, pv: string, cible = ''): Promise<AgentSignal> =>
    call(() => http().post(`signals/${encodeURIComponent(id)}/correct`, { pv, cible }), 'Correction impossible.');

export const dismissSignals = (ids: string[]): Promise<{ dismissed: number }> =>
    call(() => http().post('signals/dismiss', { signal_ids: ids }), 'Impossible d\'ignorer ces signaux.');

/** Campagne Brouillon sur les décideurs des entreprises signalées (persona des réglages) */
export const launchSignals = (ids: string[], persona: string): Promise<AutopilotCampaign> =>
    call(() => http().post('campaigns', {
        persona, mode: 'draft', test_mode: true, stop: { mode: 'manual' }, exclusions: [], signal_ids: ids,
    }), 'Lancement impossible.');
